import { Component, OnInit, OnChanges, OnDestroy, Input, SimpleChanges } from '@angular/core';

import { Subject, Subscription, debounceTime, switchMap } from 'rxjs';
import { cloneDeep } from 'lodash'; 
 
import { VehicleControlIndicator } from '../../../../../commons/models/domain/VehicleControlIndicator';
import { Vehicle } from '../../../../../commons/models/domain/Vehicle';
import { VehicleService } from '@commons/services/vehicle/vehicle.service';
import { Consts } from '../../../../../commons/consts/consts';
import { ParameterTypeEnum } from '@commons/enums/parameter-type.enum';
import { NewWebSocketHttpService } from '@shared/websocket/new-web-socket-http.service';
import { WebAccessService } from '@commons/services/web.access/web-access.service';
import { IMaxFuelVehicle } from '@src/app/interfaces/vehicle.interface';
import { NetworkType } from '@src/app/enum/network.enum';
import { RiskCategory } from '@commons/enums/vehicle.enum';
import { LanguageService } from '@shared/services/language.service';

@Component({
    selector: 'tk-vehicle-details-params-tab',
    styleUrls: ['./vehicle-details-params-tab.component.scss'],
    templateUrl: './vehicle-details-params-tab.component.html'
})
export class VehicleDetailsParamsTabComponent implements OnInit, OnChanges, OnDestroy {

    public readonly NETWORK_TYPE = NetworkType;
    public readonly RISK_CATEGORY = RiskCategory;
    /**
     *
     */
    @Input('vehicle') public set onChangeVehicle(data: Vehicle) {
        this._vehicle = { ...data } as any;
        this.setVehicleParams(cloneDeep(this._vehicle));
        if (data.Id !== this.vehicleId) {
            this.vehicleId = data.Id;
            this.riskCategory = RiskCategory.Unknown;
            // TODO: połączyć w jeden sub
            this.onChangeVehicleData(data.Id);
            this.getVehicleMaxFuelValue(data.Id);
            this.getVehicleRoamingRiskCategory(data.Id);
        }
    }

    public _vehicle: Vehicle;

    /**
     * Subject for pushing vehicle value changes
     */
    public vehicleValueChangeSubject: Subject<Vehicle> = new Subject<Vehicle>();

    /**
     * Vehicle params
     */
    private vehicleId: number;
    public params?: VehicleControlIndicator[] = [];
    public vehicleSpeed: number;
    public maxSpeedStreet?: number;
    public speed?: VehicleControlIndicator;
    public gsm?: VehicleControlIndicator;
    public voltage?: VehicleControlIndicator;
    public gps?: VehicleControlIndicator;
    public lastUpdated?: any;
    public lastUpdatedFrom?: string;
    public gpsLevelPercent: number = 100;
    public gsmLevelPercent: number = 100;
    public fuelState?: VehicleControlIndicator | null;
    public maxFuelValue: IMaxFuelVehicle;
    public riskCategory: RiskCategory;

    /**
     * Vehicle params subscription
     */
    private readonly ComponentSubsc: Subscription = new Subscription();
    private onChangeVehicleDataSubsc: Subscription;

    /**
    * Returns analog params
    */
    private analogTypes = [1, 2, 3, 4, 5, 6, 7, 10, 11, 12, 13, 14, 15, 17, 18, 20, 21, 22, 24, 25, 27, 28];
    public get analogParams(): VehicleControlIndicator[] {
        const params = this._vehicle ? this._vehicle.ControlIndicators?.filter(param => param && this.analogTypes.indexOf(param.Tp) !== -1) : [];
        return params ? params.filter(item => this.filterAnalogParams(item)).sort((a, b) => (a.Nm >= b.Nm ? 1 : -1)) : [];
    }

    /**
    * Returns  binary params
    */
    public get binaryParams(): VehicleControlIndicator[] {
        const params = this._vehicle ? this._vehicle.ControlIndicators?.filter(param => param && Consts.binaryIndicators.indexOf(param.Tp) !== -1) : [];
        return params ? params.filter(item => this.filterBinaryParams(item)).sort((a, b) => (a.Nm >= b.Nm ? 1 : -1)) : [];
    }

    /**
     *
     * @param  $vehicle
     */
    constructor(
        private $vehicle: VehicleService,
        private newWebSocketHttpService: NewWebSocketHttpService,
        private webAccessService: WebAccessService,
        public languageService: LanguageService,
    ) {}

    /**
     * Ng on init lifecycle hook
     */
    public ngOnInit(): void {
        // this.getVehicleData();
        // this.vehicleValueChangeSubject.next(this._vehicle);
    }

    /**
     * Ng on destroy lifecycle hook
     */
    public ngOnDestroy(): void {
        this.ComponentSubsc.unsubscribe();
        this.onChangeVehicleDataSubsc?.unsubscribe();
    }

    /**
     * Ng on changes lifecycle hook
     */
    public ngOnChanges(changes: SimpleChanges): void {}

    private getVehicleMaxFuelValue(vehicleId: number): void {
        const sub = this.webAccessService.GetVehicleFuelCapacity(vehicleId).subscribe(
            (data) => {
                this.maxFuelValue = data;
            }
        );
        this.ComponentSubsc.add(sub);
    }

    private getVehicleRoamingRiskCategory(vehicleId: number): void {
        const sub = this.webAccessService.GetVehicleRoamingRiskCategory(vehicleId).subscribe(
            (data) => {
                this.riskCategory = data.RiskCategory;
            }
        );
        this.ComponentSubsc.add(sub);
    }

    private onChangeVehicleData(idVehicle: number): void {
        this.onChangeVehicleDataSubsc?.unsubscribe();
        this.onChangeVehicleDataSubsc = this.newWebSocketHttpService.updatedVehicles
          .pipe(
            debounceTime(1000),
            switchMap(() => this.$vehicle.getDetails$(idVehicle)),
          )
          .subscribe(
            (dataVehicle) => {
                this._vehicle = { ...dataVehicle } as any;
                this.setVehicleParams(dataVehicle);
            }
          ); 
    }

    private setVehicleParams(dataVehicle: Vehicle): void {
        this.maxSpeedStreet = dataVehicle.LL?.SL;
        this.fuelState = null;
        this.setLastUpdateDate();
        if (dataVehicle.ControlIndicators) {
            this.setSpeedVehicle(dataVehicle.ControlIndicators);
            this.setGSM(dataVehicle.ControlIndicators);
            this.setVoltage(dataVehicle.ControlIndicators);
            this.setGPS(dataVehicle.ControlIndicators);
            this.setFuelState(dataVehicle.ControlIndicators);        
        }
    }
    
    private filterAnalogParams(param: VehicleControlIndicator): boolean {
        const ifServiceParam = param.Nm.slice(0, 1) === '#';
        if (ifServiceParam) {
            return false;
        }
        const filteredTypeOfControl = [ParameterTypeEnum.Speed, ParameterTypeEnum.GSMLevel, ParameterTypeEnum.VisibleSatellites, ParameterTypeEnum.Voltage, ParameterTypeEnum.Fuel];
        const ifFilteredTypeOfControl = filteredTypeOfControl.includes(param.Tp);
        if (ifFilteredTypeOfControl) {
            return false;
        }
        return true;
    }

    private filterBinaryParams(param: VehicleControlIndicator): boolean {
        const hiddenParamsByName = ['BZ - brak zasilania'];
        const ifHiddenParamByName = hiddenParamsByName.includes(param.Nm);
        if (ifHiddenParamByName) {
            return false;
        }
        const ifValueNull = param.LV === null;
        if (ifValueNull) {
            return false;
        }
        const ifServiceParam = param.Nm.slice(0, 1) === '#';
        if (ifServiceParam) {
            return false;
        }
       return !param.Nm.includes('OUT');
    }

    public openGoogleMaps(): void {
        const googleMapsUrl = `https://www.google.com/maps?t=k&q=${this._vehicle.LL?.Lt}+${this._vehicle.LL?.Ln}`;
        window.open(googleMapsUrl, '_blank');
    }

    public copyCoords(): void {
        navigator.clipboard.writeText(`${this._vehicle.LL?.Lt},${this._vehicle.LL?.Ln}`);
    }

    public getSpeedVehicle(params: any[]) {
        return params?.find(item => item.Tp === ParameterTypeEnum.Speed);
    }

    public copyValue(value?: string): void {
        value && navigator.clipboard.writeText(value);
    }

    private calculatePercentge(total: number, currentValue: number): number {
        return (currentValue / total) * 100;
    }

    private setFuelState(params: VehicleControlIndicator[]): void {
        this.fuelState = params.find(item => item.Tp === ParameterTypeEnum.Fuel && item.LV);
    }

    private setGSM(params: VehicleControlIndicator[]): void {
        this.gsm = params.find(p => p && p.Tp === ParameterTypeEnum.GSMLevel);
        this.gsmLevelPercent = this.calculatePercentge(32, this.gsm?.LV?.V);
        if (!this.gsm) {
            this.speed = this.createVehicleControlIndicator(ParameterTypeEnum.GSMLevel, 'GSM', 0);
        }
        else if (!this.gsm.LV) {
            this.gsm.LV = <any>{ V: 0 };
        }
    }

    private setGPS(params: VehicleControlIndicator[]): void {
        this.gps = params.find(p => p && p.Tp === ParameterTypeEnum.VisibleSatellites);
        this.gpsLevelPercent = this.calculatePercentge(12, this.gps?.LV?.V);
        if (!this.gps) {
            this.gps = this.createVehicleControlIndicator(ParameterTypeEnum.VisibleSatellites, 'GPS', 0);
        }
        else if (!this.gps.LV) {
            this.gps.LV = <any>{ V: 0 };
        }
    }

    private setVoltage(params: VehicleControlIndicator[]): void {
        this.voltage = params.find(p => p && p.Tp === ParameterTypeEnum.Voltage);
        if (!this.voltage) {
            this.voltage = this.createVehicleControlIndicator(ParameterTypeEnum.Voltage, 'Napięcie', 0);
        }
        else if (!this.voltage.LV) {
            this.voltage.LV = <any>{ V: 0 };
        }
    }

    private setSpeedVehicle(params: VehicleControlIndicator[]): void {
        this.speed = params.find(p => p && p.Tp === ParameterTypeEnum.Speed);
        if (!this.speed) {
            this.speed = this.createVehicleControlIndicator(ParameterTypeEnum.Speed, 'Prędkość', 0);
        }
        else if (!this.speed.LV) {
            this.speed.LV = <any>{ V: 0 };
        }
    }

    private createVehicleControlIndicator(paramType: number, name: string, lastValue: number): VehicleControlIndicator {
        let indicator = {} as VehicleControlIndicator;
        indicator.Tp = paramType;
        indicator.Nm = name;
        indicator.LV = <any>{ V: lastValue };
        return indicator;
    }

    private setLastUpdateDate(): void {
        if (this._vehicle) {
            const v = this._vehicle;
            const vehicleIndicators: VehicleControlIndicator[] = v.ControlIndicators as VehicleControlIndicator[];
            const paramPosition: VehicleControlIndicator | undefined = vehicleIndicators?.find(x => x.Tp == 22);
            if (paramPosition && paramPosition.LV) {
                const lastPositionTime:Date = new Date(paramPosition.LV.T);
                const n = new Date();
                this.lastUpdated = Math.ceil((n.getTime() - lastPositionTime.getTime()) / 1000);
            }
            this.lastUpdatedFrom = v.LastUpdatedFrom;
        }
    }

}
